import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { AbstractComponent, ChatCommandTypeEnum, ChatSocketService, ChatUserSetting } from 'lib-trend-core';
import { take, takeUntil } from 'rxjs/operators';
import * as ChatActions from '../state/chat.actions';
import { mySettingsSelector } from '../state/chat.selectors';
import { ChatNotificationGateService } from './chat-notification-gate.service';

/** "HH:mm" 24h clock — what `<input type="time">` produces and the backend accepts. */
const HOUR_MINUTE_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Defaults offered the first time quiet hours are enabled. */
const DEFAULT_QUIET_START = '22:00';
const DEFAULT_QUIET_END = '07:00';

/**
 * Notification preferences (SPEC-2026-07-internal-chat, T-21 · RF-36 / AC-32).
 *
 * Sends SETTINGS_UPDATE `{ quietHoursEnabled, quietHoursStart?, quietHoursEnd?,
 * soundEnabled }` over the websocket command path, patching the store
 * optimistically and rolling back on an error ack. Empty boundaries are omitted
 * from the payload — the backend validates any provided value strictly.
 *
 * Quiet hours suppress push (server-side) and in-app sound/toasts (client-side,
 * `ChatNotificationGateService`) while unread counters keep accumulating.
 */
@Component({
  templateUrl: './chat-preferences-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatPreferencesDialogComponent extends AbstractComponent implements OnInit {

  soundEnabled = true;
  quietHoursEnabled = false;
  quietHoursStart = DEFAULT_QUIET_START;
  quietHoursEnd = DEFAULT_QUIET_END;

  submitting = false;
  quietNow = false;
  companyTimezone = '';

  /** Server-known snapshot — the rollback value when the command is refused. */
  private previous: Partial<ChatUserSetting> = {};

  constructor(
    injector: Injector,
    private readonly dialogRef: MatDialogRef<ChatPreferencesDialogComponent>,
    private readonly store: Store,
    private readonly chatSocketService: ChatSocketService,
    private readonly notificationGate: ChatNotificationGateService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.companyTimezone = this.getTimezoneCompanyCurrentUser();
    this.quietNow = this.notificationGate.isQuietNow();

    // One-shot hydration — the form must not jump while it is being edited.
    this.store.select(mySettingsSelector)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((settings) => {
        this.previous = {
          soundEnabled: settings?.soundEnabled !== false,
          quietHoursEnabled: settings?.quietHoursEnabled === true,
          quietHoursStart: settings?.quietHoursStart,
          quietHoursEnd: settings?.quietHoursEnd,
        };
        this.soundEnabled = settings?.soundEnabled !== false;
        this.quietHoursEnabled = settings?.quietHoursEnabled === true;
        this.quietHoursStart = settings?.quietHoursStart || DEFAULT_QUIET_START;
        this.quietHoursEnd = settings?.quietHoursEnd || DEFAULT_QUIET_END;
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (this.submitting) return;

    const start = (this.quietHoursStart || '').trim();
    const end = (this.quietHoursEnd || '').trim();

    if (this.quietHoursEnabled && (!HOUR_MINUTE_PATTERN.test(start) || !HOUR_MINUTE_PATTERN.test(end) || start === end)) {
      this.alertService.error(this.t('chat.preferences.quietHoursRequired'));
      return;
    }

    this.submitting = true;
    this.store.dispatch(ChatActions.patchMySettings({
      patch: {
        soundEnabled: this.soundEnabled,
        quietHoursEnabled: this.quietHoursEnabled,
        quietHoursStart: start || undefined,
        quietHoursEnd: end || undefined,
      },
    }));

    // Boundaries are only sent when filled — the backend rejects an empty
    // string (it validates every provided value against "HH:mm").
    const payload: Record<string, unknown> = {
      soundEnabled: this.soundEnabled,
      quietHoursEnabled: this.quietHoursEnabled,
    };
    if (HOUR_MINUTE_PATTERN.test(start)) payload['quietHoursStart'] = start;
    if (HOUR_MINUTE_PATTERN.test(end)) payload['quietHoursEnd'] = end;

    this.chatSocketService.sendCommand(ChatCommandTypeEnum.SETTINGS_UPDATE, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.dialogRef.close(true),
        error: (ack) => {
          this.submitting = false;
          this.store.dispatch(ChatActions.patchMySettings({ patch: this.previous }));
          this.alertService.error(this.resolveAckError(ack?.errorKey));
        },
      });
  }

  private resolveAckError(errorKey?: string): string {
    if (errorKey) {
      const translated = this.t(errorKey);
      if (translated !== errorKey) return translated;
    }
    return this.t('chat.errors.commandFailed');
  }
}
